// @ts-nocheck
// ═══ Bianca Visualizer — Timeline Editor ═══
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Player } from '@remotion/player'
import { StudioComposition, type CompositionProps } from './Composition'
import type { StudioProject, LyricBlock, Section, TimelineState, SectionType } from './types'
import { createEmptyProject, SECTION_COLORS } from './types'
import { parseSRT, srtToLyricBlocks, splitBlockToWords, mergeBlocks, lyricBlocksToSRT } from './srt'
import { extractWaveformPeaks, drawWaveform } from './waveform'
import { analyzeBeats, type BeatAnalysis } from './BeatDetection'
import WavesurferTimeline from './WavesurferTimeline'
import { listProjects, loadProject, saveProject, createProject, type ProjectListItem } from './api'

// ═══ Constants ═══
const MIN_ZOOM = 20   // px per second (zoomed out)
const MAX_ZOOM = 200  // px per second (zoomed in)
const TRACK_HEIGHT = 48
const SECTION_TRACK_HEIGHT = 32
const RULER_HEIGHT = 28
const HEADER_WIDTH = 120

export default function StudioPage() {
  const [project, setProject] = useState<StudioProject>(createEmptyProject())
  const [timeline, setTimeline] = useState<TimelineState>({
    zoom: 60,
    scrollLeft: 0,
    playheadMs: 0,
    isPlaying: false,
    selectedBlockId: null,
    selectedSectionId: null,
  })
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSectionEditor, setShowSectionEditor] = useState(false)
  const [editingBlock, setEditingBlock] = useState<LyricBlock | null>(null)
  const [waveformPeaks, setWaveformPeaks] = useState<number[]>([])
  const [waveformLoading, setWaveformLoading] = useState(false)
  const [beatAnalysis, setBeatAnalysis] = useState<BeatAnalysis>({ bpm: 0, beats: [], confidence: 0 })
  const [serverProjectId, setServerProjectId] = useState<string | null>(null)
  const [projectList, setProjectList] = useState<{ owned: ProjectListItem[]; shared: ProjectListItem[]; public: ProjectListItem[] } | null>(null)
  const [showProjectBrowser, setShowProjectBrowser] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dragging, setDragging] = useState<{ blockId: string; edge: 'left' | 'right' | 'move'; startX: number; startMs: number; endMs: number } | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [renderJobId, setRenderJobId] = useState<string | null>(null)
  const [renderStatus, setRenderStatus] = useState<string | null>(null)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderUrl, setRenderUrl] = useState<string | null>(null)

  // Memoize inputProps for Remotion Player (prevents unnecessary re-renders)
  const compositionProps: CompositionProps = useMemo(() => ({ project }), [project])

  const timelineRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const waveformCanvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(0)

  const durationMs = project.audio?.durationMs || 0
  const totalWidth = (durationMs / 1000) * timeline.zoom

  // ═══ Load project list on mount ═══

  useEffect(() => {
    listProjects().then(setProjectList).catch(() => {})
  }, [])

  const handleLoadServerProject = useCallback(async (id: string) => {
    try {
      const detail = await loadProject(id)
      const pd = detail.project_data as StudioProject
      if (pd && pd.lyrics) {
        setProject(pd)
        setServerProjectId(id)
        setShowProjectBrowser(false)
      }
    } catch (err) {
      console.error('Failed to load project:', err)
    }
  }, [])

  const handleCreateNewProject = useCallback(async () => {
    try {
      const result = await createProject('Untitled Project')
      setServerProjectId(result.id)
      setProject(createEmptyProject())
      setShowProjectBrowser(false)
    } catch (err) {
      console.error('Failed to create project:', err)
    }
  }, [])

  const handleSaveToServer = useCallback(async () => {
    if (!serverProjectId) return
    setSaving(true)
    try {
      await saveProject(serverProjectId, project)
    } catch (err) {
      console.error('Failed to save:', err)
    }
    setSaving(false)
  }, [serverProjectId, project])

  // Auto-save every 30 seconds when editing a server project
  useEffect(() => {
    if (!serverProjectId) return
    const interval = setInterval(() => {
      saveProject(serverProjectId, project).catch(() => {})
    }, 30000)
    return () => clearInterval(interval)
  }, [serverProjectId, project])

  // ═══ Render ═══

  const startRender = useCallback(async () => {
    if (project.lyrics.length === 0) return
    setRenderStatus('starting')
    setRenderProgress(0)
    setRenderUrl(null)

    try {
      const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`

      const res = await fetch('/api/tech/studio/render', {
        method: 'POST', headers,
        body: JSON.stringify({ project_data: project }),
      })
      const data = await res.json()
      if (data.jobId) {
        setRenderJobId(data.jobId)
        setRenderStatus('rendering')
      } else {
        setRenderStatus('failed')
      }
    } catch {
      setRenderStatus('failed')
    }
  }, [project])

  // Poll render job status
  useEffect(() => {
    if (!renderJobId || renderStatus === 'completed' || renderStatus === 'failed') return

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`/api/tech/studio/render/${renderJobId}`)
        const job = await res.json()
        setRenderProgress(job.progress || 0)
        setRenderStatus(job.status)
        if (job.status === 'completed' && job.outputUrl) {
          setRenderUrl(job.outputUrl)
          clearInterval(poll)
        }
        if (job.status === 'failed') {
          clearInterval(poll)
        }
      } catch { /* ignore */ }
    }, 2000)

    return () => clearInterval(poll)
  }, [renderJobId, renderStatus])

  // ═══ Audio ═══

  const handleAudioImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const url = URL.createObjectURL(file)
    const audio = new Audio(url)
    audio.addEventListener('loadedmetadata', () => {
      const durationMs = Math.round(audio.duration * 1000)
      setProject(prev => ({
        ...prev,
        audio: { filename: file.name, url, durationMs },
        title: prev.title === 'Untitled Project' ? file.name.replace(/\.[^.]+$/, '') : prev.title,
      }))
      // Extract waveform peaks and detect beats in background
      setWaveformLoading(true)
      Promise.all([
        extractWaveformPeaks(url, 3000),
        analyzeBeats(url),
      ]).then(([peaks, beats]) => {
        setWaveformPeaks(peaks)
        setBeatAnalysis(beats)
        setWaveformLoading(false)
      }).catch(() => setWaveformLoading(false))
    })
  }, [])

  const handleSRTImport = useCallback((text: string) => {
    const entries = parseSRT(text)
    if (entries.length === 0) return
    const blocks = srtToLyricBlocks(entries)
    setProject(prev => ({ ...prev, lyrics: blocks }))
    setShowImportModal(false)
  }, [])

  // ═══ Playback ═══

  const togglePlay = useCallback(() => {
    if (!audioRef.current || !project.audio) return

    if (timeline.isPlaying) {
      audioRef.current.pause()
      cancelAnimationFrame(animRef.current)
      setTimeline(prev => ({ ...prev, isPlaying: false }))
    } else {
      audioRef.current.currentTime = timeline.playheadMs / 1000
      audioRef.current.play()
      setTimeline(prev => ({ ...prev, isPlaying: true }))
      const tick = () => {
        if (audioRef.current) {
          setTimeline(prev => ({
            ...prev,
            playheadMs: Math.round(audioRef.current!.currentTime * 1000),
          }))
        }
        animRef.current = requestAnimationFrame(tick)
      }
      animRef.current = requestAnimationFrame(tick)
    }
  }, [timeline.isPlaying, timeline.playheadMs, project.audio])

  const seekTo = useCallback((ms: number) => {
    setTimeline(prev => ({ ...prev, playheadMs: Math.max(0, Math.min(ms, durationMs)) }))
    if (audioRef.current) {
      audioRef.current.currentTime = ms / 1000
    }
  }, [durationMs])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      switch (e.key) {
        case ' ':
          e.preventDefault()
          togglePlay()
          break
        case 'ArrowLeft':
          e.preventDefault()
          seekTo(timeline.playheadMs - (e.shiftKey ? 5000 : 1000))
          break
        case 'ArrowRight':
          e.preventDefault()
          seekTo(timeline.playheadMs + (e.shiftKey ? 5000 : 1000))
          break
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [togglePlay, seekTo, timeline.playheadMs])

  // ═══ Waveform Drawing ═══

  useEffect(() => {
    const canvas = waveformCanvasRef.current
    if (!canvas || waveformPeaks.length === 0 || !project.audio) return

    const dpr = window.devicePixelRatio || 1
    const rect = canvas.parentElement?.getBoundingClientRect()
    if (!rect) return

    canvas.width = rect.width * dpr
    canvas.height = 64 * dpr
    canvas.style.width = `${totalWidth + 100}px`
    canvas.style.height = '64px'

    drawWaveform(canvas, waveformPeaks, {
      color: 'rgba(100, 160, 255, 0.5)',
      zoom: timeline.zoom,
      durationMs: project.audio.durationMs,
      scrollLeft: 0,
    })

    // Draw beat markers on top of waveform
    if (beatAnalysis.beats.length > 0 && project.audio.durationMs > 0) {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const dpr = window.devicePixelRatio || 1
        ctx.strokeStyle = 'rgba(255, 100, 100, 0.4)'
        ctx.lineWidth = 1 * dpr
        for (const beatMs of beatAnalysis.beats) {
          const x = ((beatMs / 1000) * timeline.zoom) * dpr
          ctx.beginPath()
          ctx.moveTo(x, 0)
          ctx.lineTo(x, canvas.height)
          ctx.stroke()
        }
      }
    }
  }, [waveformPeaks, beatAnalysis, timeline.zoom, project.audio, totalWidth])

  // ═══ Block Operations ═══

  const updateBlock = useCallback((id: string, updates: Partial<LyricBlock>) => {
    setProject(prev => ({
      ...prev,
      lyrics: prev.lyrics.map(b => b.id === id ? { ...b, ...updates } : b),
    }))
  }, [])

  const splitBlock = useCallback((id: string) => {
    setProject(prev => {
      const block = prev.lyrics.find(b => b.id === id)
      if (!block) return prev
      const words = splitBlockToWords(block)
      return {
        ...prev,
        lyrics: prev.lyrics.flatMap(b => b.id === id ? words : [b]),
      }
    })
  }, [])

  const togglePowerWord = useCallback((id: string) => {
    setProject(prev => ({
      ...prev,
      lyrics: prev.lyrics.map(b =>
        b.id === id ? { ...b, isPowerWord: !b.isPowerWord } : b
      ),
    }))
  }, [])

  // ═══ Block Drag/Resize ═══

  const handleDragStart = useCallback((e: React.MouseEvent, blockId: string, edge: 'left' | 'right' | 'move') => {
    e.stopPropagation()
    e.preventDefault()
    const block = project.lyrics.find(b => b.id === blockId)
    if (!block) return
    setDragging({ blockId, edge, startX: e.clientX, startMs: block.startMs, endMs: block.endMs })
  }, [project.lyrics])

  useEffect(() => {
    if (!dragging) return

    const handleMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragging.startX
      const deltaMs = Math.round((deltaX / timeline.zoom) * 1000)

      setProject(prev => ({
        ...prev,
        lyrics: prev.lyrics.map(b => {
          if (b.id !== dragging.blockId) return b
          if (dragging.edge === 'left') {
            const newStart = Math.max(0, dragging.startMs + deltaMs)
            return { ...b, startMs: Math.min(newStart, b.endMs - 100) }
          }
          if (dragging.edge === 'right') {
            const newEnd = Math.max(dragging.startMs + 100, dragging.endMs + deltaMs)
            return { ...b, endMs: Math.min(newEnd, durationMs || Infinity) }
          }
          // Move entire block
          const newStart = Math.max(0, dragging.startMs + deltaMs)
          const duration = dragging.endMs - dragging.startMs
          return { ...b, startMs: newStart, endMs: newStart + duration }
        }),
      }))
    }

    const handleUp = () => setDragging(null)

    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', handleUp)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [dragging, timeline.zoom, durationMs])

  // ═══ Section Operations ═══

  const addSection = useCallback((type: SectionType, startMs: number, endMs: number) => {
    const section: Section = {
      id: `sec-${Date.now()}`,
      name: type.charAt(0).toUpperCase() + type.slice(1),
      type,
      startMs,
      endMs,
      backgroundColor: SECTION_COLORS[type],
      intensity: type === 'chorus' ? 0.8 : type === 'verse' ? 0.5 : 0.6,
    }
    setProject(prev => ({ ...prev, sections: [...prev.sections, section] }))
  }, [])

  // ═══ Export ═══

  const exportSRT = useCallback(() => {
    const srt = lyricBlocksToSRT(project.lyrics)
    const blob = new Blob([srt], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${project.title}.srt`
    a.click()
    URL.revokeObjectURL(url)
  }, [project])

  const exportProject = useCallback(() => {
    const json = JSON.stringify(project, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${project.title}.studio.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [project])

  const importProject = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string) as StudioProject
        setProject(data)
      } catch { /* invalid JSON */ }
    }
    reader.readAsText(file)
  }, [])

  // ═══ Render ═══

  const msToX = (ms: number) => (ms / 1000) * timeline.zoom
  const xToMs = (x: number) => Math.round((x / timeline.zoom) * 1000)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)', color: 'var(--text-primary)' }}>
      {/* Hidden audio element */}
      {project.audio && <audio ref={audioRef} src={project.audio.url} />}

      {/* ─── Toolbar ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        flexShrink: 0,
      }}>
        <Link to="/tech" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 13 }}>
          ← Tech
        </Link>
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

        <input
          value={project.title}
          onChange={e => setProject(prev => ({ ...prev, title: e.target.value }))}
          style={{
            background: 'transparent', border: 'none', color: 'var(--text-primary)',
            fontSize: 14, fontWeight: 600, outline: 'none', width: 200,
          }}
        />

        <div style={{ flex: 1 }} />

        {/* Import buttons */}
        <label style={toolbarButtonStyle}>
          🎵 Audio
          <input type="file" accept="audio/*" onChange={handleAudioImport} style={{ display: 'none' }} />
        </label>

        <button onClick={() => setShowImportModal(true)} style={toolbarButtonStyle}>
          📝 SRT
        </button>

        <button onClick={exportSRT} style={toolbarButtonStyle} disabled={project.lyrics.length === 0}>
          ↓ Export SRT
        </button>

        <button
          onClick={startRender}
          style={{
            ...toolbarButtonStyle,
            background: renderStatus === 'rendering' ? '#E8C872' : renderStatus === 'completed' ? '#4CAF50' : 'var(--bg)',
            color: renderStatus === 'rendering' || renderStatus === 'completed' ? '#1a1a2e' : 'var(--text-primary)',
          }}
          disabled={project.lyrics.length === 0 || renderStatus === 'rendering'}
        >
          {renderStatus === 'rendering' ? `🔄 ${renderProgress}%`
            : renderStatus === 'completed' ? '✅ Done'
            : '🎬 Render'}
        </button>

        {renderUrl && (
          <a href={renderUrl} download style={{ ...toolbarButtonStyle, textDecoration: 'none' }}>
            ↓ Download MP4
          </a>
        )}

        <button onClick={serverProjectId ? handleSaveToServer : exportProject} style={toolbarButtonStyle}>
          {saving ? '⏳' : '💾'} {serverProjectId ? 'Save' : 'Export'}
        </button>

        <button onClick={() => { setShowProjectBrowser(true); setProject(createEmptyProject()); setServerProjectId(null); setWaveformPeaks([]); }} style={toolbarButtonStyle}>
          📋 Projects
        </button>

        <label style={toolbarButtonStyle}>
          📂 Load
          <input type="file" accept=".json,.studio.json" onChange={importProject} style={{ display: 'none' }} />
        </label>
      </div>

      {/* ─── Transport ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '6px 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        flexShrink: 0,
      }}>
        <button onClick={togglePlay} style={{ ...transportButton, fontSize: 18 }} disabled={!project.audio}>
          {timeline.isPlaying ? '⏸' : '▶'}
        </button>

        <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-secondary)', minWidth: 90 }}>
          {formatTime(timeline.playheadMs)} / {formatTime(durationMs)}
        </span>

        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />

        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Zoom</span>
        <input
          type="range" min={MIN_ZOOM} max={MAX_ZOOM} value={timeline.zoom}
          onChange={e => setTimeline(prev => ({ ...prev, zoom: Number(e.target.value) }))}
          style={{ width: 100 }}
        />

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{
              ...toolbarButtonStyle,
              background: showPreview ? 'var(--accent)' : 'var(--bg)',
              color: showPreview ? '#fff' : 'var(--text-primary)',
            }}
          >
            {showPreview ? '🎬 Preview On' : '🎬 Preview'}
          </button>

          {project.lyrics.length} blocks · {project.sections.length} sections
        </span>
      </div>

      {/* ─── Preview Pane ─── */}
      {showPreview && project.lyrics.length > 0 && (
        <div style={{
          borderBottom: '1px solid var(--border)', background: '#000',
          display: 'flex', justifyContent: 'center', padding: 8, flexShrink: 0,
        }}>
          <div style={{ width: 640, height: 360, borderRadius: 4, overflow: 'hidden' }}>
            <Player
              component={StudioComposition}
              inputProps={compositionProps}
              durationInFrames={Math.max(1, Math.round((durationMs / 1000) * project.visuals.fps))}
              compositionWidth={project.visuals.width}
              compositionHeight={project.visuals.height}
              fps={project.visuals.fps}
              style={{ width: '100%', height: '100%' }}
              controls
              autoPlay={false}
              clickToPlay
            />
          </div>
        </div>
      )}

      {/* ─── Main Content: Timeline + Properties ─── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ─── Timeline Area ─── */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Project Browser / Empty state */}
          {showProjectBrowser && !project.audio && project.lyrics.length === 0 && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: 32, overflow: 'auto',
            }}>
              <div style={{ maxWidth: 600, width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{ fontSize: 48, opacity: 0.3 }}>🎬</div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', marginTop: 8 }}>Bianca Visualizer</div>
                </div>

                {/* New project + import buttons */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
                  <button onClick={handleCreateNewProject} style={{ ...toolbarButtonStyle, padding: '10px 20px', fontSize: 14 }}>
                    + New Project
                  </button>
                  <label style={{ ...toolbarButtonStyle, padding: '10px 20px', fontSize: 14 }}>
                    🎵 Import Audio
                    <input type="file" accept="audio/*" onChange={handleAudioImport} style={{ display: 'none' }} />
                  </label>
                  <button onClick={() => setShowImportModal(true)} style={{ ...toolbarButtonStyle, padding: '10px 20px', fontSize: 14 }}>
                    📝 Import SRT
                  </button>
                </div>

                {/* Saved projects */}
                {projectList && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {projectList.owned.length > 0 && (
                      <ProjectSection title="Your Projects" projects={projectList.owned} onOpen={handleLoadServerProject} />
                    )}
                    {projectList.shared.length > 0 && (
                      <ProjectSection title="Shared With You" projects={projectList.shared} onOpen={handleLoadServerProject} />
                    )}
                    {projectList.public.length > 0 && (
                      <ProjectSection title="Public Projects" projects={projectList.public} onOpen={handleLoadServerProject} />
                    )}
                    {projectList.owned.length === 0 && projectList.shared.length === 0 && projectList.public.length === 0 && (
                      <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, marginTop: 16 }}>
                        No projects yet. Create a new one or import audio to get started.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline tracks */}
          {(project.audio || project.lyrics.length > 0) && (
            <div
              ref={timelineRef}
              style={{ flex: 1, overflow: 'auto', position: 'relative' }}
              onClick={(e) => {
                if (!timelineRef.current) return
                const rect = timelineRef.current.getBoundingClientRect()
                const x = e.clientX - rect.left + timelineRef.current.scrollLeft - HEADER_WIDTH
                if (x >= 0) seekTo(xToMs(x))
              }}
            >
              <div style={{ position: 'relative', minWidth: totalWidth + HEADER_WIDTH + 100, minHeight: '100%' }}>
                {/* Ruler */}
                <div style={{
                  position: 'sticky', top: 0, zIndex: 10, height: RULER_HEIGHT,
                  background: 'var(--surface)', borderBottom: '1px solid var(--border)',
                  marginLeft: HEADER_WIDTH,
                }}>
                  <TimeRuler durationMs={durationMs} zoom={timeline.zoom} />
                </div>

                {/* Sections track */}
                <div style={{ display: 'flex', height: SECTION_TRACK_HEIGHT, borderBottom: '1px solid var(--border)' }}>
                  <div style={trackHeader}>Sections</div>
                  <div style={{ position: 'relative', flex: 1 }}>
                    {project.sections.map(section => (
                      <div
                        key={section.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          setTimeline(prev => ({ ...prev, selectedSectionId: section.id, selectedBlockId: null }))
                        }}
                        style={{
                          position: 'absolute',
                          left: msToX(section.startMs),
                          width: Math.max(2, msToX(section.endMs - section.startMs)),
                          height: SECTION_TRACK_HEIGHT - 4,
                          top: 2,
                          background: section.backgroundColor,
                          borderRadius: 4,
                          opacity: 0.7,
                          cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 600, color: '#fff',
                          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                          border: timeline.selectedSectionId === section.id ? '2px solid #fff' : 'none',
                        }}
                      >
                        {section.name}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Waveform track — wavesurfer.js */}
                <div style={{ display: 'flex', minHeight: 128, borderBottom: '1px solid var(--border)' }}>
                  <div style={trackHeader}>
                    {waveformLoading ? '⏳ Audio' : '🎵 Audio'}
                  </div>
                  <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {project.audio ? (
                      <WavesurferTimeline
                        audioUrl={project.audio.url}
                        durationMs={project.audio.durationMs}
                        zoom={timeline.zoom}
                        playheadMs={timeline.playheadMs}
                        isPlaying={timeline.isPlaying}
                        sections={project.sections}
                        lyrics={project.lyrics}
                        beatAnalysis={beatAnalysis}
                        selectedBlockId={timeline.selectedBlockId}
                        onSeek={seekTo}
                        onBlockSelect={(id) => setTimeline(prev => ({ ...prev, selectedBlockId: id }))}
                        onBlockDrag={(id, startMs, endMs) => {
                          setProject(prev => ({
                            ...prev,
                            lyrics: prev.lyrics.map(b => b.id === id ? { ...b, startMs, endMs } : b),
                          }))
                        }}
                        height={128}
                      />
                    ) : (
                      <div style={{
                        height: 128, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: 'var(--text-secondary)', opacity: 0.5,
                      }}>
                        Import audio to see waveform
                      </div>
                    )}
                  </div>
                </div>

                {/* Lyrics track */}
                <div style={{ display: 'flex', minHeight: TRACK_HEIGHT * 3, borderBottom: '1px solid var(--border)' }}>
                  <div style={trackHeader}>Lyrics</div>
                  <div style={{ position: 'relative', flex: 1 }}>
                    {project.lyrics.map(block => {
                      const isSelected = timeline.selectedBlockId === block.id
                      return (
                        <div
                          key={block.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            setTimeline(prev => ({ ...prev, selectedBlockId: block.id, selectedSectionId: null }))
                            setEditingBlock(block)
                          }}
                          style={{
                            position: 'absolute',
                            left: msToX(block.startMs),
                            width: Math.max(8, msToX(block.endMs - block.startMs)),
                            height: TRACK_HEIGHT - 6,
                            top: 3,
                            background: block.isPowerWord
                              ? 'linear-gradient(135deg, #E8C872, #D4A853)'
                              : isSelected
                                ? 'var(--accent)'
                                : 'rgba(100, 140, 200, 0.5)',
                            borderRadius: 4,
                            cursor: 'pointer',
                            padding: '2px 6px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            fontSize: 11,
                            lineHeight: `${TRACK_HEIGHT - 10}px`,
                            color: '#fff',
                            border: isSelected ? '2px solid #fff' : '1px solid rgba(255,255,255,0.15)',
                            boxShadow: block.isPowerWord ? '0 0 8px rgba(232, 200, 114, 0.4)' : 'none',
                          }}
                          title={block.text}
                          onMouseDown={(e) => handleDragStart(e, block.id, 'move')}
                        >
                          {/* Left resize handle */}
                          <div
                            onMouseDown={(e) => handleDragStart(e, block.id, 'left')}
                            style={{
                              position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
                              cursor: 'col-resize', zIndex: 2,
                            }}
                          />
                          {block.text}
                          {/* Right resize handle */}
                          <div
                            onMouseDown={(e) => handleDragStart(e, block.id, 'right')}
                            style={{
                              position: 'absolute', right: 0, top: 0, bottom: 0, width: 5,
                              cursor: 'col-resize', zIndex: 2,
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Playhead */}
                {durationMs > 0 && (
                  <div style={{
                    position: 'absolute',
                    left: HEADER_WIDTH + msToX(timeline.playheadMs),
                    top: 0, bottom: 0,
                    width: 2,
                    background: '#ff4444',
                    zIndex: 20,
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      position: 'absolute', top: 0, left: -5,
                      width: 12, height: 12,
                      background: '#ff4444', borderRadius: '50%',
                    }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Properties Panel ─── */}
        <div style={{
          width: 280, borderLeft: '1px solid var(--border)', background: 'var(--surface)',
          overflow: 'auto', flexShrink: 0, padding: 12,
        }}>
          {editingBlock ? (
            <BlockPropertiesPanel
              block={editingBlock}
              onUpdate={(updates) => {
                updateBlock(editingBlock.id, updates)
                setEditingBlock(prev => prev ? { ...prev, ...updates } : null)
              }}
              onSplit={() => splitBlock(editingBlock.id)}
              onTogglePower={() => togglePowerWord(editingBlock.id)}
              onClose={() => {
                setEditingBlock(null)
                setTimeline(prev => ({ ...prev, selectedBlockId: null }))
              }}
              visuals={project.visuals}
              onUpdateVisuals={(updates) => setProject(prev => ({
                ...prev,
                visuals: { ...prev.visuals, ...updates },
              }))}
            />
          ) : (
            <ProjectPropertiesPanel
              project={project}
              serverProjectId={serverProjectId}
              onUpdateVisuals={(updates) => setProject(prev => ({
                ...prev,
                visuals: { ...prev.visuals, ...updates },
              }))}
              onAddSection={addSection}
              onUpdateTitle={(title) => setProject(prev => ({ ...prev, title }))}
              onUpdateSections={(sections) => setProject(prev => ({ ...prev, sections }))}
            />
          )}
        </div>
      </div>

      {/* ─── SRT Import Modal ─── */}
      {showImportModal && (
        <SRTImportModal
          onImport={handleSRTImport}
          onClose={() => setShowImportModal(false)}
        />
      )}
    </div>
  )
}

// ═══ Sub-components ═══

function ProjectSection({ title, projects, onOpen }: { title: string; projects: ProjectListItem[]; onOpen: (id: string) => void }) {
  return (
    <div>
      <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {projects.map(p => (
          <button
            key={p.id}
            onClick={() => onOpen(p.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%',
              color: 'var(--text-primary)',
            }}
          >
            <div style={{ fontSize: 20 }}>🎬</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{p.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {p.visibility} · {p.role} · updated {new Date(p.updated_at).toLocaleDateString()}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function TimeRuler({ durationMs, zoom }: { durationMs: number; zoom: number }) {
  if (durationMs === 0) return null

  const marks: JSX.Element[] = []
  const stepMs = zoom > 100 ? 1000 : zoom > 50 ? 5000 : 10000

  for (let ms = 0; ms <= durationMs; ms += stepMs) {
    const x = (ms / 1000) * zoom
    marks.push(
      <div key={ms} style={{ position: 'absolute', left: x }}>
        <div style={{ width: 1, height: 8, background: 'var(--border)' }} />
        <span style={{ fontSize: 9, color: 'var(--text-secondary)', marginLeft: 2 }}>
          {formatTime(ms)}
        </span>
      </div>
    )
  }

  return <div style={{ position: 'relative', height: '100%' }}>{marks}</div>
}

function BlockPropertiesPanel({
  block, onUpdate, onSplit, onTogglePower, onClose, visuals, onUpdateVisuals,
}: {
  block: LyricBlock
  onUpdate: (updates: Partial<LyricBlock>) => void
  onSplit: () => void
  onTogglePower: () => void
  onClose: () => void
  visuals: StudioProject['visuals']
  onUpdateVisuals: (updates: Partial<StudioProject['visuals']>) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Lyric Block</h3>
        <button onClick={onClose} style={{ ...smallButton, fontSize: 16, padding: '0 4px' }}>×</button>
      </div>

      {/* Text */}
      <div>
        <label style={labelStyle}>Text</label>
        <textarea
          value={block.text}
          onChange={e => onUpdate({ text: e.target.value })}
          rows={2}
          style={inputStyle}
        />
      </div>

      {/* Timing */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Start</label>
          <input
            type="text" value={formatTime(block.startMs)}
            readOnly
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 11 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>End</label>
          <input
            type="text" value={formatTime(block.endMs)}
            readOnly
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 11 }}
          />
        </div>
      </div>

      {/* Mode */}
      <div>
        <label style={labelStyle}>Display Mode</label>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => onUpdate({ mode: 'line' })}
            style={{ ...modeButton, background: block.mode === 'line' ? 'var(--accent)' : 'var(--bg)' }}
          >
            Line
          </button>
          <button
            onClick={() => onUpdate({ mode: 'word' })}
            style={{ ...modeButton, background: block.mode === 'word' ? 'var(--accent)' : 'var(--bg)' }}
          >
            Word-by-word
          </button>
        </div>
      </div>

      {/* Animation */}
      <div>
        <label style={labelStyle}>Animation</label>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {([
            ['fade', 'Fade'],
            ['stagger', 'Stagger'],
            ['elastic', 'Elastic'],
            ['typewriter', 'Typewriter'],
            ['split-scale', 'Split'],
            ['blur-reveal', 'Blur'],
            ['wave', 'Wave'],
            ['bounce', 'Bounce'],
            ['glitch', 'Glitch'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => onUpdate({ style: { ...block.style, animation: key } })}
              style={{
                ...presetButton,
                background: (block.style?.animation || visuals.defaultAnimation) === key ? 'var(--accent)' : 'var(--bg)',
                fontSize: 10,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onSplit} style={actionButton}>
          ✂ Split Words
        </button>
        <button onClick={onTogglePower} style={{
          ...actionButton,
          background: block.isPowerWord ? '#E8C872' : undefined,
          color: block.isPowerWord ? '#1a1a2e' : undefined,
        }}>
          ⚡ {block.isPowerWord ? 'Power Word' : 'Mark Power'}
        </button>
      </div>

      <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />

      {/* Global font controls */}
      <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0, color: 'var(--text-secondary)' }}>Global Style</h4>

      <div>
        <label style={labelStyle}>Font Size ({visuals.defaultFontSize}px)</label>
        <input
          type="range" min={24} max={200} value={visuals.defaultFontSize}
          onChange={e => onUpdateVisuals({ defaultFontSize: Number(e.target.value) })}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          {[48, 85, 120, 160].map(size => (
            <button key={size} onClick={() => onUpdateVisuals({ defaultFontSize: size })}
              style={{ ...presetButton, background: visuals.defaultFontSize === size ? 'var(--accent)' : 'var(--bg)' }}>
              {size === 48 ? '1x' : size === 85 ? '2x' : size === 120 ? '3x' : 'Fill'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Font Color</label>
        <input
          type="color" value={visuals.defaultFontColor}
          onChange={e => onUpdateVisuals({ defaultFontColor: e.target.value })}
          style={{ width: '100%', height: 30, cursor: 'pointer', border: 'none', background: 'none' }}
        />
      </div>

      <div>
        <label style={labelStyle}>Background Color</label>
        <input
          type="color" value={visuals.defaultBackgroundColor}
          onChange={e => onUpdateVisuals({ defaultBackgroundColor: e.target.value })}
          style={{ width: '100%', height: 30, cursor: 'pointer', border: 'none', background: 'none' }}
        />
      </div>
    </div>
  )
}

function ProjectPropertiesPanel({
  project, serverProjectId, onUpdateVisuals, onAddSection, onUpdateTitle, onUpdateSections,
}: {
  project: StudioProject
  serverProjectId: string | null
  onUpdateVisuals: (updates: Partial<StudioProject['visuals']>) => void
  onAddSection: (type: SectionType, startMs: number, endMs: number) => void
  onUpdateTitle: (title: string) => void
  onUpdateSections: (sections: StudioProject['sections']) => void
}) {
  const [sectionType, setSectionType] = useState<SectionType>('verse')
  const [visibility, setVisibility] = useState<string>('private')
  const [shareUserId, setShareUserId] = useState('')
  const [shares, setShares] = useState<{ user_id: string; role: string }[]>([])
  const [showSettings, setShowSettings] = useState(false)
  const [bgStyle, setBgStyle] = useState<'cinematic' | 'abstract' | 'minimal' | 'neon'>('cinematic')
  const [bgGenerating, setBgGenerating] = useState<string | null>(null) // section id being generated
  const [bgGeneratingAll, setBgGeneratingAll] = useState(false)

  // Load project settings from server
  useEffect(() => {
    if (!serverProjectId) return
    loadProject(serverProjectId).then(detail => {
      setVisibility(detail.visibility)
      setShares(detail.shares || [])
    }).catch(() => {})
  }, [serverProjectId])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Project Settings</h3>
        {serverProjectId && (
          <button onClick={() => setShowSettings(!showSettings)} style={{ ...smallButton, fontSize: 11 }}>
            {showSettings ? '▼ Style' : '▶ Access'}
          </button>
        )}
      </div>

      {/* Access Control Settings */}
      {showSettings && serverProjectId && (
        <>
          <div>
            <label style={labelStyle}>Project Title</label>
            <input
              value={project.title}
              onChange={e => onUpdateTitle(e.target.value)}
              onBlur={() => {
                if (serverProjectId) {
                  import('./api').then(({ updateProjectSettings }) =>
                    updateProjectSettings(serverProjectId, { title: project.title })
                  )
                }
              }}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Visibility</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['private', 'shared', 'public'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => {
                    setVisibility(v)
                    import('./api').then(({ updateProjectSettings }) =>
                      updateProjectSettings(serverProjectId, { visibility: v })
                    )
                  }}
                  style={{
                    ...modeButton,
                    background: visibility === v ? 'var(--accent)' : 'var(--bg)',
                    fontSize: 10,
                  }}
                >
                  {v === 'private' ? '🔒 Private' : v === 'shared' ? '👥 Shared' : '🌍 Public'}
                </button>
              ))}
            </div>
          </div>

          {/* Share with user */}
          <div>
            <label style={labelStyle}>Share With</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={shareUserId}
                onChange={e => setShareUserId(e.target.value)}
                placeholder="User ID or name"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => {
                  if (!shareUserId.trim()) return
                  import('./api').then(({ shareProject }) =>
                    shareProject(serverProjectId, shareUserId.trim()).then(() => {
                      setShares(prev => [...prev, { user_id: shareUserId.trim(), role: 'editor' }])
                      setShareUserId('')
                      setVisibility('shared')
                    })
                  )
                }}
                style={actionButton}
                disabled={!shareUserId.trim()}
              >
                Add
              </button>
            </div>
          </div>

          {/* Current shares */}
          {shares.length > 0 && (
            <div>
              <label style={labelStyle}>Shared With</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shares.map(s => (
                  <div key={s.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, padding: '2px 4px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{s.user_id}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{s.role}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
        </>
      )}

      {/* Quick section add */}
      {project.audio && (
        <>
          <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0, color: 'var(--text-secondary)' }}>Add Section</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(['intro', 'verse', 'prechorus', 'chorus', 'bridge', 'outro'] as SectionType[]).map(type => (
              <button
                key={type}
                onClick={() => setSectionType(type)}
                style={{
                  ...presetButton,
                  background: sectionType === type ? SECTION_COLORS[type] : 'var(--bg)',
                  color: sectionType === type ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {type}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 10, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
            Click on the timeline to place sections. Sections set per-section background colors and effect intensity.
          </p>
        </>
      )}

      {/* AI Background Generation */}
      {project.sections.length > 0 && (
        <>
          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
          <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0, color: 'var(--text-secondary)' }}>AI Backgrounds</h4>

          <div>
            <label style={labelStyle}>Style</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['cinematic', 'abstract', 'minimal', 'neon'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setBgStyle(s)}
                  style={{
                    ...presetButton,
                    background: bgStyle === s ? 'var(--accent)' : 'var(--bg)',
                    fontSize: 10,
                    textTransform: 'capitalize',
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={async () => {
              if (bgGeneratingAll) return
              setBgGeneratingAll(true)
              try {
                const { generateBackground } = await import('./api')
                const updated = [...project.sections]
                for (let i = 0; i < updated.length; i++) {
                  const section = updated[i]
                  setBgGenerating(section.id)
                  // Gather lyrics that fall within this section
                  const sectionLyrics = project.lyrics
                    .filter(b => b.startMs >= section.startMs && b.startMs < section.endMs)
                    .map(b => b.text)
                    .join(' ') || section.name
                  const result = await generateBackground(sectionLyrics, bgStyle, section.name, section.type)
                  if (result.ok && result.url) {
                    updated[i] = { ...updated[i], backgroundImage: result.url }
                  }
                }
                onUpdateSections(updated)
              } catch (err) {
                console.error('Background generation failed:', err)
              } finally {
                setBgGenerating(null)
                setBgGeneratingAll(false)
              }
            }}
            disabled={bgGeneratingAll}
            style={{
              ...actionButton,
              background: bgGeneratingAll ? 'var(--border)' : '#7c4dff',
              color: '#fff',
              opacity: bgGeneratingAll ? 0.6 : 1,
            }}
          >
            {bgGeneratingAll
              ? `Generating${bgGenerating ? ` (${project.sections.find(s => s.id === bgGenerating)?.name || '...'})` : '...'}`
              : `Generate All Backgrounds (${project.sections.length})`}
          </button>

          {/* Per-section background status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {project.sections.map(section => (
              <div key={section.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: section.backgroundImage ? '#4caf50' : 'var(--border)',
                  flexShrink: 0,
                }} />
                <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{section.name}</span>
                {section.backgroundImage && (
                  <button
                    onClick={() => {
                      const updated = project.sections.map(s =>
                        s.id === section.id ? { ...s, backgroundImage: undefined } : s
                      )
                      onUpdateSections(updated)
                    }}
                    style={{ ...smallButton, fontSize: 9, padding: '0 3px', color: '#ff5252' }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />

      <h4 style={{ fontSize: 12, fontWeight: 600, margin: 0, color: 'var(--text-secondary)' }}>Global Style</h4>

      <div>
        <label style={labelStyle}>Font Size ({project.visuals.defaultFontSize}px)</label>
        <input
          type="range" min={24} max={200} value={project.visuals.defaultFontSize}
          onChange={e => onUpdateVisuals({ defaultFontSize: Number(e.target.value) })}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          {[48, 85, 120, 160].map(size => (
            <button key={size} onClick={() => onUpdateVisuals({ defaultFontSize: size })}
              style={{ ...presetButton, background: project.visuals.defaultFontSize === size ? 'var(--accent)' : 'var(--bg)' }}>
              {size === 48 ? '1x' : size === 85 ? '2x' : size === 120 ? '3x' : 'Fill'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Font Color</label>
        <input
          type="color" value={project.visuals.defaultFontColor}
          onChange={e => onUpdateVisuals({ defaultFontColor: e.target.value })}
          style={{ width: '100%', height: 30, cursor: 'pointer', border: 'none', background: 'none' }}
        />
      </div>

      <div>
        <label style={labelStyle}>Background Color</label>
        <input
          type="color" value={project.visuals.defaultBackgroundColor}
          onChange={e => onUpdateVisuals({ defaultBackgroundColor: e.target.value })}
          style={{ width: '100%', height: 30, cursor: 'pointer', border: 'none', background: 'none' }}
        />
      </div>

      <div>
        <label style={labelStyle}>Font Family</label>
        <select
          value={project.visuals.defaultFontFamily}
          onChange={e => onUpdateVisuals({ defaultFontFamily: e.target.value })}
          style={inputStyle}
        >
          <option value="'Caveat', cursive">Caveat (Handwritten)</option>
          <option value="'Georgia', serif">Georgia (Serif)</option>
          <option value="'Inter', sans-serif">Inter (Sans)</option>
          <option value="'Dancing Script', cursive">Dancing Script</option>
          <option value="monospace">Monospace</option>
        </select>
      </div>

      <div>
        <label style={labelStyle}>Resolution</label>
        <select
          value={`${project.visuals.width}x${project.visuals.height}`}
          onChange={e => {
            const [w, h] = e.target.value.split('x').map(Number)
            onUpdateVisuals({ width: w, height: h })
          }}
          style={inputStyle}
        >
          <option value="1920x1080">1080p (1920×1080)</option>
          <option value="1080x1920">Vertical (1080×1920)</option>
          <option value="1080x1080">Square (1080×1080)</option>
        </select>
      </div>
    </div>
  )
}

function SRTImportModal({ onImport, onClose }: { onImport: (text: string) => void; onClose: () => void }) {
  const [text, setText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setText(reader.result as string)
    reader.readAsText(file)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--surface)', borderRadius: 12, padding: 24,
          width: 500, maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: 12,
          border: '1px solid var(--border)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Import SRT</h3>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => fileInputRef.current?.click()} style={actionButton}>
            📁 Choose File
          </button>
          <input ref={fileInputRef} type="file" accept=".srt" onChange={handleFile} style={{ display: 'none' }} />
        </div>

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Paste SRT content here, or use the file picker above..."
          style={{ ...inputStyle, height: 200, resize: 'vertical', fontFamily: 'monospace', fontSize: 11 }}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={actionButton}>Cancel</button>
          <button
            onClick={() => onImport(text)}
            disabled={!text.trim()}
            style={{ ...actionButton, background: 'var(--accent)', color: '#fff', opacity: text.trim() ? 1 : 0.5 }}
          >
            Import
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══ Helpers ═══

function formatTime(ms: number): string {
  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  const frac = Math.floor((ms % 1000) / 100)
  return `${m}:${s.toString().padStart(2, '0')}.${frac}`
}

// ═══ Styles ═══

const toolbarButtonStyle: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-primary)',
  display: 'flex', alignItems: 'center', gap: 4,
}

const transportButton: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-primary)', padding: 4,
}

const trackHeader: React.CSSProperties = {
  width: HEADER_WIDTH, flexShrink: 0, padding: '4px 8px',
  fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
  borderRight: '1px solid var(--border)',
  display: 'flex', alignItems: 'center',
  background: 'var(--surface)', position: 'sticky', left: 0, zIndex: 5,
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2,
  display: 'block',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 12,
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  color: 'var(--text-primary)', outline: 'none',
  boxSizing: 'border-box',
}

const smallButton: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', fontSize: 12,
}

const modeButton: React.CSSProperties = {
  flex: 1, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
  border: '1px solid var(--border)', borderRadius: 4,
  color: 'var(--text-primary)',
}

const presetButton: React.CSSProperties = {
  padding: '3px 8px', fontSize: 10, cursor: 'pointer',
  border: '1px solid var(--border)', borderRadius: 4,
  color: 'var(--text-primary)',
}

const actionButton: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
  padding: '6px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--text-primary)',
}
